<script>
import SaveButton from "@components/elements/SaveButton.vue";

export default {
  components: { SaveButton },

  props: {
    settings: {
      type: Object,
      required: true
    },
    // Resolves false to keep the form unsaved (e.g. a declined confirmation).
    beforeSave: {
      type: Function,
      default: null
    },
    options: {
      type: Array,
      required: true,
      // Each option should have:
      // - key: string (property name in settings)
      // - type: 'toggle' | 'select' | 'input' | 'textarea' | 'custom' (rendered by the slot named after its key)
      // - label: string
      // - description: string
      // - componentId: string (unique ID)
      // - options: array (for select type)
      // - condition: function (optional, returns boolean to show/hide)
      // - placeholder: string (for input/textarea)
      // - minWidth: string (for select/input, default: '150px')
    }
  },

  data() {
    return {
      localSettings: {},
      originalSettings: {}
    };
  },

  computed: {
    hasChanges() {
      return JSON.stringify(this.localSettings) !== JSON.stringify(this.originalSettings);
    },

    validationErrors() {
      const errors = {};
      this.options.forEach(option => {
        if (option.validate && this.isOptionVisible(option)) {
          const error = option.validate(this.localSettings[option.key], this.localSettings);
          if (error) errors[option.key] = error;
        }
      });
      return errors;
    },

    hasValidationErrors() {
      return Object.keys(this.validationErrors).length > 0;
    },

    settingsWithDefaults() {
      const defaults = {};
      this.options.forEach(option => {
        if (option.default !== undefined) {
          defaults[option.key] = option.default;
        }
      });
      return {
        ...defaults,
        ...(this.settings || {})
      };
    }
  },

  watch: {
    settings: {
      handler() {
        this.updateLocalSettings();
      },
      deep: true,
      immediate: true
    },
    options: {
      handler() {
        this.updateLocalSettings();
      },
      deep: true
    }
  },

  created() {
    this.updateLocalSettings();
  },

  methods: {
    updateLocalSettings() {
      const merged = { ...this.settingsWithDefaults };
      this.localSettings = { ...merged };
      this.originalSettings = { ...merged };
    },

    isOptionVisible(option) {
      return !option.condition || option.condition(this.localSettings);
    },

    async saveSettings() {
      if (this.hasValidationErrors) return;
      if (this.beforeSave && !(await this.beforeSave({ ...this.localSettings }))) return;
      this.$emit('save', { ...this.localSettings });
      this.originalSettings = { ...this.localSettings };
    }
  }
};
</script>
<template>
  <div>
    <div v-for="option in options" :key="option.key" v-show="!option.condition || option.condition(localSettings)" class="mb-4">
      <slot
        v-if="option.type === 'custom'"
        :name="option.key"
        :settings="localSettings"
        :error="validationErrors[option.key]"
      />

      <!-- Textarea / Input: Stack vertically -->
      <div v-else-if="option.type === 'textarea' || option.type === 'input'" class="tw-flex tw-flex-col">
        <div class="tw-mb-2">
          <label :for="option.componentId" class="mb-0">{{ option.label }}</label>
          <small v-if="option.description" class="form-text text-muted d-block mt-1">
            {{ option.description }}
          </small>
          <small v-if="option.note" class="form-text text-muted d-block mt-1" style="font-style: italic; font-size: 0.75rem;">
            {{ option.note }}
          </small>
        </div>
        <b-form-textarea
          v-if="option.type === 'textarea'"
          :id="option.componentId"
          v-model="localSettings[option.key]"
          :placeholder="option.placeholder"
          :rows="option.rows || 3"
          :disabled="option.disabled || false"
          :class="{ 'disabled-textarea': option.disabled }"
          class="tw-w-full"
        ></b-form-textarea>
        <b-form-input
          v-else
          :id="option.componentId"
          v-model="localSettings[option.key]"
          :type="option.inputType || 'text'"
          :placeholder="option.placeholder"
          :maxlength="option.maxLength || undefined"
          :state="validationErrors[option.key] ? false : null"
          class="tw-w-full"
        ></b-form-input>
        <b-form-invalid-feedback v-if="validationErrors[option.key]" class="d-block mt-1" :state="false">
          {{ validationErrors[option.key] }}
        </b-form-invalid-feedback>
      </div>
      
      <!-- Toggle / Select: Side by side layout -->
      <div v-else>
        <div class="d-flex justify-content-between align-items-center">
          <div class="flex-grow-1 mr-5">
            <label :for="option.componentId" class="mb-0">{{ option.label }}</label>
            <small v-if="option.description" class="form-text text-muted d-block mt-1">
              {{ option.description }}
            </small>
            <small v-if="option.note" class="form-text text-muted d-block mt-1" style="font-style: italic; font-size: 0.75rem;">
              {{ option.note }}
            </small>
          </div>
          <div class="flex-shrink-0">
            <!-- Toggle Switch -->
            <b-form-checkbox
              v-if="option.type === 'toggle'"
              :id="option.componentId"
              v-model="localSettings[option.key]"
              switch
              :name="option.componentId"
            ></b-form-checkbox>

            <!-- Select Dropdown -->
            <b-form-select
              v-else-if="option.type === 'select'"
              :id="option.componentId"
              v-model="localSettings[option.key]"
              :options="option.options"
              :style="`min-width: ${option.minWidth || '150px'};`"
            ></b-form-select>
          </div>
        </div>
      </div>
    </div>

    <div class="text-right">
      <SaveButton :disabled="!hasChanges || hasValidationErrors" data-testid="settings-form-save" @click="saveSettings" />
    </div>
  </div>
</template>

<style scoped>
.disabled-textarea {
  background-color: #f5f5f5 !important;
  opacity: 0.7 !important;
  cursor: not-allowed !important;
  border-color: #d0d0d0 !important;
}

.disabled-textarea::placeholder {
  color: #999 !important;
  opacity: 0.6 !important;
}
</style>

